import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

/**
 * Admin: set each restaurant's BILLING MODEL.
 *  • subscription — current ₹2360/mo plan + ₹10k threshold; at payout: TDS + 3% settlement fee.
 *  • commission   — no subscription/threshold; at payout: TDS + flat commission% on gross, no
 *                   settlement fee. GST: exclusive (rate% + GST) or inclusive (rate% flat).
 */
function BillingModels() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getBillingModels();
      setRows((r.data?.data || []).map((x) => ({ ...x, draft: { ...x.billingModel } })));
    } catch (e) { setMsg('Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDraft = (id, k, v) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, draft: { ...r.draft, [k]: v } } : r));

  const save = async (r) => {
    setSavingId(r.id); setMsg('');
    try {
      const payload = { type: r.draft.type };
      if (r.draft.type === 'commission') {
        payload.commissionRate = Number(r.draft.commissionRate) || 10;
        payload.commissionGstMode = r.draft.commissionGstMode || 'exclusive';
      }
      await adminAPI.setRestaurantBillingModel(r.id, payload);
      setMsg(`${r.name}: saved (${payload.type}${payload.type === 'commission' ? ` ${payload.commissionRate}% ${payload.commissionGstMode}` : ''})`);
      await load();
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSavingId(null); }
  };

  const setFunding = async (r, funding) => {
    setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, competitorMatchFunding: funding } : x));
    try {
      await adminAPI.setCompetitorMatchFunding(r.id, funding);
      setMsg(`${r.name}: Special Discount funded by ${funding}`);
    } catch (e) { setMsg(e.response?.data?.message || 'Funding update failed'); await load(); }
  };

  const setExempt = async (r, exempt) => {
    setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, competitorMatchExempt: exempt } : x));
    try {
      await adminAPI.setCompetitorMatchExempt(r.id, exempt);
      setMsg(`${r.name}: Special Discount ${exempt ? 'EXEMPTED (won’t apply)' : 're-enabled'}`);
    } catch (e) { setMsg(e.response?.data?.message || 'Exemption update failed'); await load(); }
  };

  const setPartial = async (r, partial) => {
    setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, competitorMatchPartial: partial } : x));
    try {
      await adminAPI.setCompetitorMatchPartial(r.id, partial);
      setMsg(`${r.name}: partial-match ${partial ? 'ENABLED (fires on ≥1 match, imputes unmatched +₹15)' : 'disabled (full match required)'}`);
    } catch (e) { setMsg(e.response?.data?.message || 'Partial-match update failed'); await load(); }
  };

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, marginBottom: 10, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };
  const sel = { padding: '7px 9px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 };

  const filtered = rows.filter((r) => !query || r.name.toLowerCase().includes(query.toLowerCase()) || (r.phone || '').includes(query));

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>🧾 Restaurant Billing Models</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 14px' }}>
          <b>Subscription:</b> ₹2360/mo + ₹10k threshold; payout deducts TDS + 3% settlement fee. &nbsp;
          <b>Commission:</b> no subscription/threshold; payout deducts TDS + commission% on gross (no settlement fee).
          Commission GST — <i>exclusive</i>: rate% + GST; <i>inclusive</i>: rate% flat (GST baked in).
        </p>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name / phone…" style={{ ...sel, width: 320, marginBottom: 14 }} />
        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669' }}>{msg}</div>}
        {loading ? <div style={{ padding: 16 }}>Loading…</div> : filtered.map((r) => {
          const d = r.draft || {};
          const dirty = d.type !== r.billingModel.type || (d.type === 'commission' && (Number(d.commissionRate) !== r.billingModel.commissionRate || d.commissionGstMode !== r.billingModel.commissionGstMode));
          return (
            <div key={r.id} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontWeight: 700 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{r.phone} · current: <b>{r.billingModel.type}</b>{r.billingModel.type === 'commission' ? ` ${r.billingModel.commissionRate}% ${r.billingModel.commissionGstMode}` : ''}</div>
                </div>
                <select style={sel} value={d.type} onChange={(e) => setDraft(r.id, 'type', e.target.value)}>
                  <option value="subscription">Subscription (₹2360 + 3% settlement)</option>
                  <option value="commission">Commission (flat %)</option>
                </select>
                {d.type === 'commission' && (
                  <>
                    <input type="number" style={{ ...sel, width: 70 }} value={d.commissionRate ?? 10} onChange={(e) => setDraft(r.id, 'commissionRate', e.target.value)} /> <span style={{ fontSize: 13, color: '#64748B' }}>%</span>
                    <select style={sel} value={d.commissionGstMode || 'exclusive'} onChange={(e) => setDraft(r.id, 'commissionGstMode', e.target.value)}>
                      <option value="exclusive">+ GST (exclusive)</option>
                      <option value="inclusive">incl. GST (inclusive)</option>
                    </select>
                  </>
                )}
                <button onClick={() => save(r)} disabled={savingId === r.id || !dirty} style={{ padding: '8px 16px', background: dirty ? '#16a34a' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: dirty ? 'pointer' : 'default' }}>
                  {savingId === r.id ? '…' : 'Save'}
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #eef2f7', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, color: r.competitorMatchExempt ? '#b91c1c' : '#334155', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!r.competitorMatchExempt} onChange={(e) => setExempt(r, e.target.checked)} />
                  🚫 Exempt from "Special Discount"
                </label>
                <label title="Fire the Special Discount on a PARTIAL match (≥1 item matched on Zomato); unmatched items are imputed at our price + ₹15" style={{ fontSize: 13, color: r.competitorMatchPartial ? '#7c3aed' : '#334155', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, cursor: r.competitorMatchExempt ? 'default' : 'pointer', opacity: r.competitorMatchExempt ? 0.4 : 1 }}>
                  <input type="checkbox" disabled={!!r.competitorMatchExempt} checked={!!r.competitorMatchPartial} onChange={(e) => setPartial(r, e.target.checked)} />
                  🧩 Partial-match (≥1 item)
                </label>
                <span style={{ fontSize: 13, color: r.competitorMatchExempt ? '#cbd5e1' : '#334155', fontWeight: 600 }}>· funded by:</span>
                <select style={{ ...sel, opacity: r.competitorMatchExempt ? 0.5 : 1 }} disabled={!!r.competitorMatchExempt} value={r.competitorMatchFunding || 'platform'} onChange={(e) => setFunding(r, e.target.value)}>
                  <option value="platform">Platform (we absorb it)</option>
                  <option value="restaurant">Restaurant (they absorb it)</option>
                </select>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{r.competitorMatchExempt ? 'skipped for this restaurant · ₹20 delivery floor still applies' : 'saves instantly · future orders only'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BillingModels;
